import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { authContext } from "../../context/AuthContext.js";
import api from "../../services/axiosInterceptor.js";
import PutData from "../../services/PutData.js";
import DeleteData from "../../services/DeleteData.js";
import {
    layoutsEqual,
    normalizeLayout,
    patchLayoutsMirror,
    readLayoutsMirror,
    snapshotFromState,
    writeLayoutsMirror,
} from "./gridLayoutStorage.js";

export const GRID_LAYOUTS_QUERY_KEY = ["grid-layouts"];

async function fetchGridLayouts() {
    const response = await api.get("api/grid-layouts");
    const layouts = response?.data?.layouts;
    return layouts && typeof layouts === "object" ? layouts : {};
}

export function prefetchGridLayouts(queryClient) {
    return queryClient.prefetchQuery({
        queryKey: GRID_LAYOUTS_QUERY_KEY,
        queryFn: fetchGridLayouts,
        staleTime: 5 * 60 * 1000,
    });
}

function defaultState(defaults = {}) {
    return {
        sortModel: Array.isArray(defaults.sortModel) ? defaults.sortModel : [],
        columnVisibilityModel:
            defaults.columnVisibilityModel && typeof defaults.columnVisibilityModel === "object"
                ? { ...defaults.columnVisibilityModel }
                : {},
        columnWidths:
            defaults.columnWidths && typeof defaults.columnWidths === "object"
                ? { ...defaults.columnWidths }
                : {},
    };
}

function applySavedToState(defaults, saved) {
    const base = defaultState(defaults);
    const layout = normalizeLayout(saved);
    if (!layout) return base;
    return {
        sortModel: layout.sortModel.length ? layout.sortModel : base.sortModel,
        columnVisibilityModel: {
            ...base.columnVisibilityModel,
            ...layout.columnVisibilityModel,
        },
        columnWidths: {
            ...base.columnWidths,
            ...layout.columnWidths,
        },
    };
}

/**
 * Sync-read the layout for a grid from localStorage (before first paint / listUrl).
 * Use for InvoiceGrid sort hydration to avoid a row-fetch waterfall.
 */
export function peekGridLayout(gridId, defaults, userKey) {
    const mirror = readLayoutsMirror(userKey);
    return applySavedToState(defaults, mirror[gridId]);
}

export default function useGridLayout(gridId, defaults = {}) {
    const { userName, isAuthenticated } = useContext(authContext) || {};
    const userKey = userName || "anon";
    const queryClient = useQueryClient();
    const defaultsRef = useRef(defaults);
    defaultsRef.current = defaults;

    const initial = useMemo(
        () => peekGridLayout(gridId, defaults, userKey),
        // Sync hydrate once per grid/user; defaults are code constants.
        [gridId, userKey]
    );

    const [sortModel, setSortModel] = useState(initial.sortModel);
    const [columnVisibilityModel, setColumnVisibilityModel] = useState(
        initial.columnVisibilityModel
    );
    const [committedWidths, setCommittedWidths] = useState(initial.columnWidths);
    const [draftWidths, setDraftWidths] = useState(initial.columnWidths);
    const [savedSnapshot, setSavedSnapshot] = useState(() =>
        normalizeLayout(readLayoutsMirror(userKey)[gridId])
    );
    const [saving, setSaving] = useState(false);
    const skipNextServerApply = useRef(false);
    const dirtyRef = useRef(false);
    const lastServerJson = useRef("");

    const { data: serverLayouts } = useQuery({
        queryKey: GRID_LAYOUTS_QUERY_KEY,
        queryFn: fetchGridLayouts,
        enabled: Boolean(isAuthenticated),
        staleTime: 5 * 60 * 1000,
    });

    const currentSnapshot = useMemo(
        () =>
            snapshotFromState({
                sortModel,
                columnVisibilityModel,
                columnWidths: draftWidths,
            }),
        [sortModel, columnVisibilityModel, draftWidths]
    );

    const defaultsKey = JSON.stringify(defaults);
    const defaultSnapshot = useMemo(
        () => snapshotFromState(defaultState(defaults)),
        // defaultsKey captures inline default object identity
        [defaultsKey, defaults]
    );

    const isDirty = useMemo(() => {
        const baseline = savedSnapshot || defaultSnapshot;
        return !layoutsEqual(currentSnapshot, baseline);
    }, [currentSnapshot, savedSnapshot, defaultSnapshot]);

    useEffect(() => {
        dirtyRef.current = isDirty;
    }, [isDirty]);

    useEffect(() => {
        if (!serverLayouts || typeof serverLayouts !== "object") return;
        writeLayoutsMirror(userKey, serverLayouts);
        if (skipNextServerApply.current) {
            skipNextServerApply.current = false;
            return;
        }
        const saved = normalizeLayout(serverLayouts[gridId]);
        const serverJson = JSON.stringify(saved);
        if (serverJson === lastServerJson.current) return;
        lastServerJson.current = serverJson;
        setSavedSnapshot(saved);
        if (dirtyRef.current) return;
        const next = applySavedToState(defaultsRef.current, saved);
        setSortModel(next.sortModel);
        setColumnVisibilityModel(next.columnVisibilityModel);
        setCommittedWidths(next.columnWidths);
        setDraftWidths(next.columnWidths);
    }, [serverLayouts, gridId, userKey]);

    const onSortModelChange = useCallback((next) => {
        setSortModel(Array.isArray(next) ? next : []);
    }, []);

    const onColumnVisibilityModelChange = useCallback((next) => {
        setColumnVisibilityModel(next && typeof next === "object" ? next : {});
    }, []);

    const onColumnWidthChange = useCallback((params) => {
        const field = params?.colDef?.field || params?.field;
        const width = params?.width;
        if (!field || !Number.isFinite(width)) return;
        setDraftWidths((prev) => ({ ...prev, [field]: width }));
    }, []);

    const applyWidths = useCallback(
        (columns) => {
            if (!Array.isArray(columns) || !Object.keys(committedWidths).length) {
                return columns;
            }
            return columns.map((col) => {
                const w = committedWidths[col.field];
                if (!Number.isFinite(w) || w <= 0) return col;
                const next = { ...col, width: w };
                if (next.flex != null) {
                    const { flex, ...rest } = next;
                    return rest;
                }
                return next;
            });
        },
        [committedWidths]
    );

    const save = useCallback(async () => {
        setSaving(true);
        try {
            const layout = currentSnapshot;
            const result = await PutData(layout, `grid-layouts/${gridId}`);
            if (!result.success) {
                return { success: false, message: result?.data?.message || "Save failed." };
            }
            const saved = normalizeLayout(result.data?.layout || layout);
            skipNextServerApply.current = true;
            queryClient.setQueryData(GRID_LAYOUTS_QUERY_KEY, (prev) => {
                const next = { ...(prev || {}), [gridId]: saved };
                writeLayoutsMirror(userKey, next);
                return next;
            });
            patchLayoutsMirror(userKey, gridId, saved);
            setSavedSnapshot(saved);
            setCommittedWidths(saved.columnWidths || {});
            setDraftWidths(saved.columnWidths || {});
            return { success: true };
        } finally {
            setSaving(false);
        }
    }, [currentSnapshot, gridId, queryClient, userKey]);

    const reset = useCallback(async () => {
        setSaving(true);
        try {
            const result = await DeleteData(`grid-layouts/${gridId}`);
            if (!result.success) {
                return { success: false, message: "Reset failed." };
            }
            const next = defaultState(defaultsRef.current);
            skipNextServerApply.current = true;
            queryClient.setQueryData(GRID_LAYOUTS_QUERY_KEY, (prev) => {
                const copy = { ...(prev || {}) };
                delete copy[gridId];
                writeLayoutsMirror(userKey, copy);
                return copy;
            });
            patchLayoutsMirror(userKey, gridId, null);
            setSavedSnapshot(null);
            setSortModel(next.sortModel);
            setColumnVisibilityModel(next.columnVisibilityModel);
            setCommittedWidths(next.columnWidths);
            setDraftWidths(next.columnWidths);
            return { success: true };
        } finally {
            setSaving(false);
        }
    }, [gridId, queryClient, userKey]);

    return {
        sortModel,
        onSortModelChange,
        columnVisibilityModel,
        onColumnVisibilityModelChange,
        committedWidths,
        draftWidths,
        onColumnWidthChange,
        applyWidths,
        isDirty,
        saving,
        save,
        reset,
        hasSaved: Boolean(savedSnapshot),
    };
}
